import {
  PRESETS,
  type PresetKey,
  type ShrinkProgress,
  type ShrinkResult,
  formatBytes,
  shrinkPdf,
} from "./shrinker";

const TAPE_ITEMS = [
  "MODEL SS-3000 ▪ U.S. PAT. PEND.",
  "SAVING INTERPERS EVERYWHERE",
  "TOURNAMENT GRADE",
  "STOA APPROVED FOR HOME USE*",
  "*BY US, IN OUR HOME",
  "NITOC READY",
  "FILE SIZE IS NOT YOUR FATE",
  "100MB CAP CRUSHED",
  "PRESERVES YOUR HIGHLIGHTS",
  "FOR THE OVERPREPARED",
  "RUNS IN YOUR BROWSER",
  "ZERO UPLOADS",
  "A FORTE PRODUCTION",
];

const STRIP_ITEMS = [
  "REAL COMPRESSION",
  "RUNS IN YOUR BROWSER",
  "PROTECTS YOUR HIGHLIGHTS",
  "FREE FOREVER",
  "NO UPLOADS",
  "TOURNAMENT TESTED",
  "100MB UNDER, GUARANTEED†",
];

interface AppState {
  file: File | null;
  preset: PresetKey;
  busy: boolean;
  result: ShrinkResult | null;
}

const state: AppState = {
  file: null,
  preset: "tournament",
  busy: false,
  result: null,
};

export function renderApp(root: HTMLElement) {
  root.innerHTML = template();

  bindAnimatedCounter(root);
  bindTool(root);
  bindFaq(root);
}

/* ============================================================
   Template
   ============================================================ */

function template(): string {
  const tape = repeatTape(TAPE_ITEMS);
  const strip = repeatTape(STRIP_ITEMS);

  return /* html */ `
    <div class="shell">

      <!-- TOP TAPE -->
      <div class="toptape">
        <div class="toptape__track">
          ${tape.map((t) => `<span class="toptape__item">${t}</span>`).join("")}
        </div>
      </div>

      <!-- HERO -->
      <header class="section hero">
        ${rosetteSvg()}
        <div class="stamp stamp--hero">
          PATENT
          PENDING
          <span class="stamp__small">FILED IN A SHOEBOX</span>
        </div>

        <p class="hero__overhead">Forte Industries presents</p>
        <h1 class="hero__wordmark">Script<br/>Shrinker<br/>3000</h1>
        <p class="hero__sub">Tournament-Grade <em>PDF Compression</em></p>
        <p class="hero__tagline">Saving interpers everywhere.</p>

        <figure class="hero__device">
          <img src="./hero-device.png" alt="The Script Shrinker 3000 device" />
          <figcaption>MODEL SS-3000 ▪ ACTUAL UNIT MAY DIFFER</figcaption>
        </figure>

        <div class="hero__crt" aria-label="Demonstration counter">
          <div class="hero__counter">
            <div class="hero__counter-cell">
              <div class="hero__counter-label">Before</div>
              <div class="hero__counter-num hero__counter-num--before" data-counter="before">487</div>
              <div class="hero__counter-label">megabytes</div>
            </div>
            <div class="hero__counter-arrow">▶</div>
            <div class="hero__counter-cell">
              <div class="hero__counter-label">After</div>
              <div class="hero__counter-num hero__counter-num--after" data-counter="after">42</div>
              <div class="hero__counter-label">megabytes</div>
            </div>
          </div>
        </div>

        <div class="hero__cta">
          <button class="btn btn--primary" data-scroll-to="tool">Engage Compressor</button>
          <a class="btn btn--ghost" href="#how">How It Works</a>
        </div>
      </header>

      <!-- STRIP -->
      <div class="strip">
        <div class="strip__track">
          ${strip.map((t) => `<span class="strip__item">${t}</span>`).join("")}
        </div>
      </div>

      <!-- EVIDENCE -->
      <section class="section evidence">
        <p class="section__overhead">Documented results</p>
        <h2 class="section__title">The proof is in the kilobytes.</h2>

        <figure class="evidence__panel">
          <img src="./before-after.png" alt="A 487 megabyte stack of paper compressed down to a 42 megabyte card" />
          <figcaption>Fig. 1 — Typical compression ratios on a 47-page Stoa interp script.</figcaption>
        </figure>

        <figure class="evidence__panel">
          <img src="./highlight-rescue.png" alt="A page with invisible highlights on a Windows judge laptop is rasterized so that the highlights are visible everywhere" />
          <figcaption>Fig. 2 — Cross-platform highlight integrity (patent applied for).</figcaption>
        </figure>
      </section>

      <!-- FEATURES -->
      <section class="section" id="how">
        <p class="section__overhead">Three patented innovations</p>
        <h2 class="section__title">Built for the<br/>submission portal era.</h2>
        <p class="section__lede">
          Stoa and NITOC tournaments require interp scripts as scanned, highlighted PDFs.
          The portal caps uploads at 100&nbsp;megabytes. Your script is 487. We do the math.
        </p>

        <div class="features">

          <article class="feature">
            <div class="feature__icon">⚡</div>
            <div class="feature__num">001 / SELECTIVE COMPRESSION</div>
            <h3 class="feature__title">Smart Page Triage</h3>
            <p class="feature__body">
              Most "shrinkers" nuke your whole file and return a blurry mess.
              SCRIPT SHRINKER 3000 inspects each page individually. Crisp vector
              text? Untouched. Bloated 80-megabyte scan of <em>The Hiding Place</em>?
              Rasterized to a perfectly readable JPEG. Best of both worlds, every time.
            </p>
          </article>

          <article class="feature">
            <div class="feature__icon">🖍️</div>
            <div class="feature__num">002 / HIGHLIGHT RESCUE</div>
            <h3 class="feature__title">Macs vs Judges, Solved.</h3>
            <p class="feature__body">
              Highlights drawn in Preview.app sometimes turn invisible on a judge's
              Windows laptop. Devastating. Our process bakes your highlights directly
              into the page pixels — guaranteeing they show up on every device, every
              PDF reader, every time. Forever.
            </p>
          </article>

          <article class="feature">
            <div class="feature__icon">🛡️</div>
            <div class="feature__num">003 / ZERO-UPLOAD ARCHITECTURE</div>
            <h3 class="feature__title">Your Script Never Leaves.</h3>
            <p class="feature__body">
              Every byte of compression happens inside your browser tab. Your PDF
              is never uploaded. Never logged. Never seen by us, your ISP, or any
              cloud anywhere. Disconnect your internet halfway through — it'll
              still finish. Try that with Adobe.
            </p>
          </article>

        </div>
      </section>

      <!-- TESTIMONIALS -->
      <section class="section">
        <p class="section__overhead">Endorsed by working competitors</p>
        <h2 class="section__title">"It just worked."</h2>

        <div class="testimonials">
          <article class="tcard">
            <div class="tcard__stars">★★★★★</div>
            <p class="tcard__quote">
              "The portal kept rejecting my 380MB script three minutes before
              the deadline. SHRINK button. 38 seconds. Submitted. I qualified."
            </p>
            <div class="tcard__byline">
              <div class="tcard__avatar" style="background-image:url('./testimonial-brayden.png')"></div>
              <div>
                <strong>Brayden T.</strong><br/>
                <small>Open Interp ▪ Region 4</small>
              </div>
            </div>
          </article>

          <article class="tcard">
            <div class="tcard__stars">★★★★★</div>
            <p class="tcard__quote">
              "I'm a Mac user. Three judges last year said they 'couldn't see my
              highlights.' Used SCRIPT SHRINKER. Zero comments. Won finals."
            </p>
            <div class="tcard__byline">
              <div class="tcard__avatar" style="background-image:url('./testimonial-magnolia.png')"></div>
              <div>
                <strong>Magnolia R.</strong><br/>
                <small>Dramatic Interp ▪ NITOC '25</small>
              </div>
            </div>
          </article>

          <article class="tcard">
            <div class="tcard__stars">★★★★★</div>
            <p class="tcard__quote">
              "I run a club of 24 students. Submission week used to be eight nights
              of phone calls about file sizes. Now it's <em>none</em>. I sleep again."
            </p>
            <div class="tcard__byline">
              <div class="tcard__avatar" style="background-image:url('./coach-dale.png')"></div>
              <div>
                <strong>Coach Dale K.</strong><br/>
                <small>Club Director ▪ 17 yrs Stoa</small>
              </div>
            </div>
          </article>
        </div>
      </section>

      <!-- THE TOOL -->
      <section class="section" id="tool">
        <p class="section__overhead">Operator console</p>
        <h2 class="section__title">Engage the compressor.</h2>
        <p class="section__lede">
          Drop a PDF. Pick a profile. Press the button. Everything runs in your
          browser — your file is never uploaded.
        </p>

        <div class="tool" data-serial="${randomSerial()}">
          <span class="tool__rivet tool__rivet--tl"></span>
          <span class="tool__rivet tool__rivet--tr"></span>
          <span class="tool__rivet tool__rivet--bl"></span>
          <span class="tool__rivet tool__rivet--br"></span>

          <label class="drop" data-drop>
            <input type="file" accept="application/pdf,.pdf" data-file-input />
            <div class="drop__icon">▼</div>
            <p class="drop__main" data-drop-main>Drop PDF here</p>
            <p class="drop__sub" data-drop-sub>or click to select ▪ up to ~1GB</p>
          </label>

          <div class="presets" data-presets>
            ${(Object.keys(PRESETS) as PresetKey[]).map((key) => {
              const p = PRESETS[key];
              return `
                <button class="preset ${key === state.preset ? "preset--active" : ""}" data-preset="${key}">
                  <div class="preset__label">${p.label}</div>
                  <div class="preset__sub">${p.sublabel}</div>
                  <div class="preset__specs">${p.rasterDpi} DPI ▪ Q${Math.round(p.jpegQuality * 100)} ▪ ≥${formatBytes(p.rasterizeThresholdBytes)}</div>
                </button>
              `;
            }).join("")}
          </div>

          <div class="tool__actions">
            <button class="btn btn--primary" data-shrink disabled>Shrink it</button>
            <button class="btn btn--ghost hidden" data-download>↓ Download</button>
            <button class="btn hidden" data-reset>Start over</button>
          </div>

          <div class="progress" data-progress>
            <div class="progress__phase" data-progress-phase>STANDBY</div>
            <div class="progress__msg" data-progress-msg>Awaiting input</div>
            <div class="progress__pages" data-progress-pages></div>
          </div>

          <div class="report" data-report>
            <div class="report__headline">Compression complete.</div>
            <div class="report__bigstat">
              <div>
                <div class="report__bignum report__bignum--before" data-report-before>—</div>
                <div class="report__biglabel">Before</div>
              </div>
              <div class="hero__counter-arrow">▶</div>
              <div>
                <div class="report__bignum report__bignum--after" data-report-after>—</div>
                <div class="report__biglabel">After</div>
              </div>
            </div>
            <div class="report__pct" data-report-pct></div>
            <div class="report__breakdown" data-report-breakdown></div>
          </div>
        </div>
      </section>

      <!-- FAQ -->
      <section class="section">
        <p class="section__overhead">Frequently asked</p>
        <h2 class="section__title">Yeah but really though.</h2>

        <div class="faq">
          <details class="faq__item">
            <summary class="faq__q">Will my judge actually be able to read this?</summary>
            <div class="faq__a">Yes. The Tournament preset rasterizes scanned pages at 150 DPI — the same effective resolution as a clean photocopy. Vector text pages aren't touched at all, so they remain perfectly sharp.</div>
          </details>

          <details class="faq__item">
            <summary class="faq__q">Why is my friend's "shrunk" file blurry but mine isn't?</summary>
            <div class="faq__a">Most tools rasterize every page indiscriminately, including pages that were already lean vector text. SCRIPT SHRINKER 3000 only rasterizes pages that exceed a size threshold — so vector pages stay crisp and only the bloated scan pages get compressed.</div>
          </details>

          <details class="faq__item">
            <summary class="faq__q">Does this preserve my highlights?</summary>
            <div class="faq__a">Yes — even better than the original in many cases. Highlights drawn in Preview.app on macOS are sometimes invisible to other PDF readers (especially the older Adobe versions some judges use). Because we rasterize highlighted pages to JPEG, the highlight becomes part of the image. Every reader, every device, sees them.</div>
          </details>

          <details class="faq__item">
            <summary class="faq__q">Is my script uploaded anywhere?</summary>
            <div class="faq__a">No. Everything runs in your browser using local CPU. You can disconnect from the internet after the page loads — it'll still finish. We never see your file.</div>
          </details>

          <details class="faq__item">
            <summary class="faq__q">What if it's still too big?</summary>
            <div class="faq__a">Drop down to the Email or Tiny preset. If you're still over, the source scans were almost certainly captured at an absurd resolution — message us and we'll add a manual override slider.</div>
          </details>

          <details class="faq__item">
            <summary class="faq__q">Why is this free?</summary>
            <div class="faq__a">Forte built it for the speech &amp; debate community. Costs us nothing to host. Pass it along.</div>
          </details>
        </div>
      </section>

      <!-- FOOTER -->
      <footer class="footer">
        ${forteMarkSvg()}
        <div class="footer__mission">Because the only thing that should fail to upload is your nerves.</div>
        <div class="footer__mark">A <span>FORTE</span> PRODUCTION</div>
        <div class="footer__line">SCRIPTSHRINKER3000.FORTEFORENSICS.COM</div>
        <div class="footer__legal">
          † "Guaranteed under 100MB" subject to source-file containing scans recognizable as text by a reasonable human. SCRIPT SHRINKER 3000 is not affiliated with Stoa USA or NITOC. Highlight rescue not legal advice. No actual patents are pending. "Region 4" is made up. The device pictured is fictional. Be excellent to each other.
        </div>
      </footer>

    </div>
  `;
}

/* ============================================================
   Animated counter — counts down on scroll into view
   ============================================================ */

function bindAnimatedCounter(root: HTMLElement) {
  const before = root.querySelector<HTMLElement>('[data-counter="before"]');
  const after = root.querySelector<HTMLElement>('[data-counter="after"]');
  if (!before || !after) return;

  const targetBefore = 487;
  const targetAfter = 42;
  const duration = 1800;

  const animate = () => {
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      // Glitchy decrement: fluctuate the "before" downward, lock at 487
      before.textContent = Math.round(targetBefore - eased * 0).toString();
      after.textContent = Math.round(eased * targetAfter).toString();
      if (t < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };

  // Run once on load with a small delay for drama
  setTimeout(animate, 400);
}

/* ============================================================
   Tool wiring
   ============================================================ */

function bindTool(root: HTMLElement) {
  const drop = root.querySelector<HTMLElement>("[data-drop]")!;
  const fileInput = root.querySelector<HTMLInputElement>("[data-file-input]")!;
  const dropMain = root.querySelector<HTMLElement>("[data-drop-main]")!;
  const dropSub = root.querySelector<HTMLElement>("[data-drop-sub]")!;
  const presets = root.querySelector<HTMLElement>("[data-presets]")!;
  const shrinkBtn = root.querySelector<HTMLButtonElement>("[data-shrink]")!;
  const downloadBtn = root.querySelector<HTMLButtonElement>("[data-download]")!;
  const resetBtn = root.querySelector<HTMLButtonElement>("[data-reset]")!;
  const progress = root.querySelector<HTMLElement>("[data-progress]")!;
  const progressPhase = root.querySelector<HTMLElement>("[data-progress-phase]")!;
  const progressMsg = root.querySelector<HTMLElement>("[data-progress-msg]")!;
  const progressPages = root.querySelector<HTMLElement>("[data-progress-pages]")!;
  const report = root.querySelector<HTMLElement>("[data-report]")!;
  const reportBefore = root.querySelector<HTMLElement>("[data-report-before]")!;
  const reportAfter = root.querySelector<HTMLElement>("[data-report-after]")!;
  const reportPct = root.querySelector<HTMLElement>("[data-report-pct]")!;
  const reportBreakdown = root.querySelector<HTMLElement>("[data-report-breakdown]")!;

  // Smooth scroll for hero CTA
  root.querySelectorAll<HTMLElement>("[data-scroll-to]").forEach((el) => {
    el.addEventListener("click", () => {
      const target = root.querySelector(`#${el.dataset.scrollTo}`);
      target?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  });

  /* ----- file selection ----- */

  const setFile = (f: File | null) => {
    state.file = f;
    state.result = null;
    if (f) {
      dropMain.textContent = f.name;
      dropSub.textContent = formatBytes(f.size);
      shrinkBtn.disabled = false;
      report.classList.remove("report--active");
      downloadBtn.classList.add("hidden");
      resetBtn.classList.add("hidden");
    } else {
      dropMain.textContent = "Drop PDF here";
      dropSub.textContent = "or click to select ▪ up to ~1GB";
      shrinkBtn.disabled = true;
      progress.classList.remove("progress--active");
      report.classList.remove("report--active");
      downloadBtn.classList.add("hidden");
      resetBtn.classList.add("hidden");
    }
  };

  fileInput.addEventListener("change", () => {
    const f = fileInput.files?.[0];
    if (f) setFile(f);
  });

  ["dragenter", "dragover"].forEach((evt) => {
    drop.addEventListener(evt, (e) => {
      e.preventDefault();
      drop.classList.add("drop--active");
    });
  });
  ["dragleave", "drop"].forEach((evt) => {
    drop.addEventListener(evt, (e) => {
      e.preventDefault();
      drop.classList.remove("drop--active");
    });
  });
  drop.addEventListener("drop", (e) => {
    const dt = (e as DragEvent).dataTransfer;
    const f = dt?.files?.[0];
    if (f && f.type === "application/pdf") setFile(f);
  });

  /* ----- preset selection ----- */

  presets.addEventListener("click", (e) => {
    const target = (e.target as HTMLElement).closest<HTMLElement>("[data-preset]");
    if (!target) return;
    const key = target.dataset.preset as PresetKey;
    state.preset = key;
    presets.querySelectorAll<HTMLElement>(".preset").forEach((p) => {
      p.classList.toggle("preset--active", p.dataset.preset === key);
    });
  });

  /* ----- the actual button ----- */

  shrinkBtn.addEventListener("click", async () => {
    if (!state.file || state.busy) return;
    state.busy = true;
    shrinkBtn.disabled = true;
    progress.classList.add("progress--active");
    report.classList.remove("report--active");
    progressPages.innerHTML = "";

    try {
      const buf = new Uint8Array(await state.file.arrayBuffer());
      const opts = PRESETS[state.preset];
      const result = await shrinkPdf(buf, opts, (p) =>
        updateProgress(p, progressPhase, progressMsg, progressPages),
      );
      state.result = result;
      showReport(result, reportBefore, reportAfter, reportPct, reportBreakdown, report);
      downloadBtn.classList.remove("hidden");
      resetBtn.classList.remove("hidden");
    } catch (err) {
      progressPhase.textContent = "ERROR";
      progressMsg.textContent = (err as Error).message ?? "Compression failed";
      console.error(err);
    } finally {
      state.busy = false;
      shrinkBtn.disabled = false;
    }
  });

  downloadBtn.addEventListener("click", () => {
    if (!state.result || !state.file) return;
    const blob = new Blob([state.result.bytes as BlobPart], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = suggestName(state.file.name);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  });

  resetBtn.addEventListener("click", () => {
    fileInput.value = "";
    setFile(null);
    progress.classList.remove("progress--active");
    progressPages.innerHTML = "";
  });
}

function updateProgress(
  p: ShrinkProgress,
  phaseEl: HTMLElement,
  msgEl: HTMLElement,
  pagesEl: HTMLElement,
) {
  phaseEl.textContent = p.phase.toUpperCase();
  msgEl.textContent = p.message;

  // Lazy-build the page squares once we know the count
  if (pagesEl.childElementCount !== p.totalPages && p.totalPages > 0) {
    pagesEl.innerHTML = "";
    for (let i = 0; i < p.totalPages; i++) {
      const sq = document.createElement("span");
      sq.className = "progress__page";
      sq.dataset.idx = String(i);
      pagesEl.appendChild(sq);
    }
  }

  const squares = pagesEl.children;
  if (p.phase === "rasterizing" || p.phase === "analyzing") {
    for (let i = 0; i < squares.length; i++) {
      squares[i].classList.toggle("progress__page--current", i === p.pageIndex);
    }
  }
  if (p.phase === "rasterizing") {
    squares[p.pageIndex]?.classList.add("progress__page--raster");
    squares[p.pageIndex]?.classList.remove("progress__page--current");
  }
  if (p.phase === "assembling" || p.phase === "done") {
    // Pages not flagged as raster get the "kept" color
    for (let i = 0; i < squares.length; i++) {
      const sq = squares[i] as HTMLElement;
      sq.classList.remove("progress__page--current");
      if (!sq.classList.contains("progress__page--raster")) {
        sq.classList.add("progress__page--keep");
      }
    }
  }
}

function showReport(
  r: ShrinkResult,
  beforeEl: HTMLElement,
  afterEl: HTMLElement,
  pctEl: HTMLElement,
  breakdownEl: HTMLElement,
  reportRoot: HTMLElement,
) {
  beforeEl.textContent = formatBytes(r.originalSize);
  afterEl.textContent = formatBytes(r.finalSize);
  const pct = Math.max(0, Math.round((1 - r.finalSize / r.originalSize) * 100));
  const rasterized = r.pages.filter((p) => p.rasterized).length;
  const kept = r.pages.length - rasterized;
  pctEl.textContent = `↓ ${pct}% smaller`;
  breakdownEl.textContent = `${kept} pages kept verbatim ▪ ${rasterized} pages rasterized at high quality`;
  reportRoot.classList.add("report--active");
}

function suggestName(orig: string): string {
  const base = orig.replace(/\.pdf$/i, "");
  return `${base}-shrunk.pdf`;
}

/* ============================================================
   FAQ — native <details> handles itself, but we collapse siblings
   ============================================================ */

function bindFaq(root: HTMLElement) {
  const items = root.querySelectorAll<HTMLDetailsElement>(".faq__item");
  items.forEach((item) => {
    item.addEventListener("toggle", () => {
      if (item.open) {
        items.forEach((other) => {
          if (other !== item) other.open = false;
        });
      }
    });
  });
}

/* ============================================================
   Helpers
   ============================================================ */

function repeatTape<T>(items: T[]): T[] {
  // Duplicate so the marquee can loop seamlessly
  return [...items, ...items];
}

function randomSerial(): string {
  const block = () => Math.floor(Math.random() * 9000 + 1000).toString();
  return `${block()}-${block()}`;
}

/* ============================================================
   Inline SVGs
   ============================================================ */

function rosetteSvg(): string {
  return /* html */ `
    <svg class="badge-rosette" viewBox="0 0 130 130" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <defs>
        <radialGradient id="rgRoset" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stop-color="#ff7ac6"/>
          <stop offset="100%" stop-color="#ff2e93"/>
        </radialGradient>
      </defs>
      <g transform="translate(65,65)">
        <g>
          ${Array.from({ length: 16 })
            .map((_, i) => {
              const angle = (i * 360) / 16;
              return `<polygon points="0,-58 8,-46 -8,-46" fill="url(#rgRoset)" transform="rotate(${angle})" />`;
            })
            .join("")}
        </g>
        <circle r="40" fill="#0a0e27" stroke="#c0c0c0" stroke-width="2"/>
        <circle r="34" fill="none" stroke="#00f0ff" stroke-width="1" stroke-dasharray="2 3"/>
        <text text-anchor="middle" y="-8" fill="#f4e5c2" font-family="'Space Grotesk', sans-serif" font-weight="700" font-size="9" letter-spacing="1.4">AS SEEN AT</text>
        <text text-anchor="middle" y="8" fill="#ff2e93" font-family="'Russo One', sans-serif" font-size="13">REGIONALS</text>
        <text text-anchor="middle" y="22" fill="#00f0ff" font-family="'Space Mono', monospace" font-size="9">★ ★ ★ ★ ★</text>
      </g>
    </svg>
  `;
}

function forteMarkSvg(): string {
  return /* html */ `
    <svg width="0" height="0" style="position:absolute" aria-hidden="true">
      <defs></defs>
    </svg>
  `;
}
